import { useCallback, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Retenir ou l'on en etait, ecran par ecran.
 *
 * Un onglet est une route : en changer demonte l'ecran, et sa zone
 * defilante repart donc du haut. On lit vingt posts, on va voir un
 * message, on revient — et il faut tout redefiler. Meme chose en
 * ressortant d'une sous-page.
 *
 * La position est retenue par adresse, dans une carte qui vit le temps de
 * la session, et restauree avant la peinture pour que le saut ne se voie
 * jamais. Une adresse jamais visitee s'ouvre en haut, forcement.
 *
 * On ne distingue pas l'aller du retour : la barre d'onglets navigue en
 * avant, et c'est justement le cas qu'il fallait rattraper.
 */
const positions = new Map<string, number>();

/**
 * Ou l'on en etait dans un fil de discussion.
 *
 * `ecart` est la distance au bas, jamais une position absolue : un
 * message qui arrive rallonge le fil par le bas et decalerait tout. A
 * zero, on reste colle au plus recent.
 *
 * `dernier` est la date du dernier message vu en partant. C'est ce qui
 * permet de distinguer les deux cas au retour : rien de neuf, on reprend
 * ou l'on etait ; quelqu'un a ecrit, on descend le lire. Sans cette
 * date, garder sa place revenait a rouvrir une conversation en ignorant
 * le message qui venait d'arriver.
 *
 * Elle vit hors des composants a dessein : c'est precisement quand
 * l'ecran se remonte qu'elle sert, et un etat interne disparaitrait avec
 * lui.
 */
export const placesDeLecture = new Map<
  string,
  {
    ecart: number;
    dernier: number;
    /**
     * La position absolue, telle quelle.
     *
     * C'est **elle** qui repose la lecture au retour, et non l'ecart au
     * bas : un message arrive pendant l'absence allonge le fil par le
     * bas, et garder la distance au bas remontait alors le contenu de la
     * hauteur du nouveau message. Ce qu'on lisait doit rester ou on
     * l'avait laisse ; ce qui s'ajoute en dessous ne le concerne pas.
     *
     * L'ecart, lui, garde son role : c'est la bonne mesure quand la vue
     * change de taille sous le clavier.
     */
    haut: number;
  }
>();

export function clearScrollMemory(): void {
  positions.clear();
  placesDeLecture.clear();
}

export function useScrollMemory<T extends HTMLElement>(
  /**
   * Retenir la position, ou toujours ouvrir en haut.
   *
   * Un formulaire ne se reprend pas la ou on l'avait laisse : on y entre
   * pour ecrire quelque chose de neuf, et retrouver le milieu du
   * questionnaire donne l'impression d'une page a moitie remplie. Une
   * liste, elle, doit rendre sa place.
   */
  retenir = true,
) {
  const location = useLocation();
  const cle = location.pathname + location.search;
  const zone = useRef<T | null>(null);

  /*
    Une ref-fonction plutot qu'un objet : elle est appelee des que le
    noeud existe, donc avant la premiere peinture. Un effet, lui, passe
    apres — et le haut de la liste aurait le temps de s'afficher.
  */
  const ref = useCallback(
    (noeud: T | null) => {
      zone.current = noeud;
      if (noeud) noeud.scrollTop = retenir ? (positions.get(cle) ?? 0) : 0;
    },
    [cle, retenir],
  );

  useLayoutEffect(() => {
    const noeud = zone.current;
    if (!noeud) return;

    /*
      Le contenu arrive apres coup — le reseau, puis le rendu. Tant que la
      hauteur est insuffisante, le navigateur ramene le defilement a ce
      qu'il peut ; on repose donc la position a chaque changement de
      taille, jusqu'a ce qu'elle tienne. Au-dela d'une seconde on lache :
      c'est que l'ecran a change de forme, et forcer irait contre ce que
      l'utilisateur a sous les yeux.
    */
    const vise = retenir ? (positions.get(cle) ?? 0) : 0;
    let observateur: ResizeObserver | null = null;
    let fin: ReturnType<typeof setTimeout> | null = null;

    if (vise > 0) {
      noeud.scrollTop = vise;
      observateur = new ResizeObserver(() => {
        if (noeud.scrollTop < vise) noeud.scrollTop = vise;
      });
      observateur.observe(noeud);
      fin = setTimeout(() => observateur?.disconnect(), 1000);
    }

    const noter = () => {
      if (retenir) positions.set(cle, noeud.scrollTop);
    };
    noeud.addEventListener('scroll', noter, { passive: true });

    return () => {
      noter();
      noeud.removeEventListener('scroll', noter);
      observateur?.disconnect();
      if (fin) clearTimeout(fin);
    };
  }, [cle, retenir]);

  return ref;
}
